import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * SMS Settings Tab Component
 * Handles Twilio SMS integration configuration
 */
export const SMSSettingsTab = ({ settings, setSettings, API }) => {
  if (!settings) return null;

  return (
    <div className="sms-settings">
      <Card>
        <CardHeader>
          <CardTitle>Twilio SMS Integration</CardTitle>
          <CardDescription>Configure SMS messaging to send text messages to customers</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {/* Enable/Disable Toggle */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              padding: '1rem', 
              backgroundColor: settings.twilio_enabled ? '#f0fdf4' : '#f9fafb', 
              borderRadius: '8px', 
              border: `1px solid ${settings.twilio_enabled ? '#86efac' : '#e5e7eb'}` 
            }}>
              <div>
                <div style={{ fontWeight: '600' }}>SMS Enabled</div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  {settings.twilio_enabled ? '✅ SMS messaging is active' : '❌ SMS messaging is disabled'}
                </div>
              </div>
              <Button 
                variant={settings.twilio_enabled ? 'destructive' : 'default'}
                onClick={async () => {
                  try {
                    await axios.put(`${API}/settings`, { twilio_enabled: !settings.twilio_enabled });
                    setSettings({ ...settings, twilio_enabled: !settings.twilio_enabled });
                    toast.success(settings.twilio_enabled ? 'SMS disabled' : 'SMS enabled');
                  } catch (error) {
                    toast.error('Failed to update setting');
                  }
                }}
                data-testid="toggle-sms-btn"
              >
                {settings.twilio_enabled ? 'Disable SMS' : 'Enable SMS'}
              </Button>
            </div>

            {/* Twilio Credentials */}
            <div>
              <h4 style={{ fontSize: '0.875rem', fontWeight: '600', marginBottom: '1rem' }}>Twilio Credentials</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <Label htmlFor="twilio_sid">Account SID</Label>
                  <Input
                    id="twilio_sid"
                    type="text"
                    placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    defaultValue={settings.twilio_account_sid || ''}
                    onBlur={async (e) => {
                      if (e.target.value !== settings.twilio_account_sid) {
                        try {
                          await axios.put(`${API}/settings`, { twilio_account_sid: e.target.value });
                          setSettings({ ...settings, twilio_account_sid: e.target.value });
                          toast.success('Account SID saved');
                        } catch (error) {
                          toast.error('Failed to save');
                        }
                      }
                    }}
                    data-testid="twilio-sid-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Find this in your Twilio Console dashboard
                  </p>
                </div>
                <div>
                  <Label htmlFor="twilio_token">Auth Token</Label>
                  <Input
                    id="twilio_token"
                    type="password"
                    placeholder="Your Twilio Auth Token"
                    defaultValue={settings.twilio_auth_token || ''}
                    onBlur={async (e) => {
                      if (e.target.value !== settings.twilio_auth_token) {
                        try {
                          await axios.put(`${API}/settings`, { twilio_auth_token: e.target.value });
                          setSettings({ ...settings, twilio_auth_token: e.target.value });
                          toast.success('Auth Token saved');
                        } catch (error) {
                          toast.error('Failed to save');
                        }
                      }
                    }}
                    data-testid="twilio-token-input"
                  />
                </div>
                <div>
                  <Label htmlFor="twilio_from">From Phone Number</Label>
                  <Input
                    id="twilio_from"
                    type="text"
                    placeholder="+61412345678"
                    defaultValue={settings.twilio_from_number || ''}
                    onBlur={async (e) => {
                      if (e.target.value !== settings.twilio_from_number) {
                        try {
                          await axios.put(`${API}/settings`, { twilio_from_number: e.target.value });
                          setSettings({ ...settings, twilio_from_number: e.target.value });
                          toast.success('From Number saved');
                        } catch (error) {
                          toast.error('Failed to save');
                        }
                      }
                    }}
                    data-testid="twilio-from-input"
                  />
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Your Twilio phone number (include country code, e.g., +61 for Australia)
                  </p>
                </div>
              </div>
            </div>

            {/* Test Connection */}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await axios.post(`${API}/sms/test-connection`);
                    toast.success(`Connection successful! Account: ${response.data.account_name}`);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Connection failed');
                  }
                }}
                data-testid="test-twilio-btn"
              >
                Test Connection
              </Button>
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                Verify your Twilio credentials are correct
              </span>
            </div>

            {/* Info Box */}
            <div style={{ padding: '1rem', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
              <h4 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#1e40af' }}>📱 How to get Twilio credentials</h4>
              <ol style={{ fontSize: '0.875rem', color: '#1e40af', paddingLeft: '1.25rem', margin: 0 }}>
                <li>Sign up at <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>twilio.com</a></li>
                <li>Get your Account SID and Auth Token from the Console Dashboard</li>
                <li>Buy a phone number (~$1.50/month for Australian number)</li>
                <li>Add credit to your account (~$0.06 per SMS)</li>
              </ol>
            </div>

            {/* SMS Reply Forwarding */}
            <SMSReplyForwardingSection settings={settings} setSettings={setSettings} API={API} />

            {/* SMS Templates */}
            <SMSTemplatesSection settings={settings} setSettings={setSettings} API={API} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * SMS Reply Forwarding Sub-section
 */
const SMSReplyForwardingSection = ({ settings, setSettings, API }) => (
  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>📨 SMS Reply Forwarding</h4>
    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
      When customers reply to your SMS messages, their replies can be forwarded to your email inbox.
    </p>
    
    <div style={{ display: 'grid', gap: '1rem' }}>
      {/* Enable/Disable Toggle */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        padding: '1rem', 
        backgroundColor: settings.twilio_reply_forward_enabled ? '#f0fdf4' : '#f9fafb', 
        borderRadius: '8px', 
        border: `1px solid ${settings.twilio_reply_forward_enabled ? '#86efac' : '#e5e7eb'}` 
      }}>
        <div>
          <div style={{ fontWeight: '600' }}>Reply Forwarding</div>
          <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
            {settings.twilio_reply_forward_enabled ? '✅ Replies will be forwarded to email' : '❌ Reply forwarding is disabled'}
          </div>
        </div>
        <Button
          variant={settings.twilio_reply_forward_enabled ? 'destructive' : 'default'}
          size="sm"
          onClick={async () => {
            try {
              await axios.put(`${API}/settings`, { twilio_reply_forward_enabled: !settings.twilio_reply_forward_enabled });
              setSettings({ ...settings, twilio_reply_forward_enabled: !settings.twilio_reply_forward_enabled });
              toast.success(settings.twilio_reply_forward_enabled ? 'Reply forwarding disabled' : 'Reply forwarding enabled');
            } catch (error) {
              toast.error('Failed to update setting');
            }
          }}
          data-testid="toggle-reply-forward-btn"
        >
          {settings.twilio_reply_forward_enabled ? 'Disable' : 'Enable'}
        </Button>
      </div>
      
      {/* Forward Email Address */}
      {settings.twilio_reply_forward_enabled && (
        <div>
          <Label htmlFor="reply_forward_email">Forward replies to email</Label>
          <Input
            id="reply_forward_email"
            type="email"
            placeholder="your-email@example.com"
            defaultValue={settings.twilio_reply_forward_email || ''}
            onBlur={async (e) => {
              if (e.target.value !== settings.twilio_reply_forward_email) {
                try {
                  await axios.put(`${API}/settings`, { twilio_reply_forward_email: e.target.value });
                  setSettings({ ...settings, twilio_reply_forward_email: e.target.value });
                  toast.success('Forward email saved');
                } catch (error) {
                  toast.error('Failed to save email');
                }
              }
            }}
            data-testid="reply-forward-email-input"
            style={{ marginTop: '0.5rem' }}
          />
          <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            SMS replies from customers will be sent to this email address
          </p>
        </div>
      )}
      
      {/* Webhook URL Info */}
      <div style={{ padding: '1rem', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
        <h5 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#92400e' }}>⚙️ Twilio Webhook Setup Required</h5>
        <p style={{ fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
          To receive SMS replies, you need to configure a webhook in Twilio:
        </p>
        <ol style={{ fontSize: '0.875rem', color: '#92400e', paddingLeft: '1.25rem', margin: 0 }}>
          <li>Go to Twilio Console → Phone Numbers → Manage → Active Numbers</li>
          <li>Click on your phone number</li>
          <li>Under "Messaging", find "A MESSAGE COMES IN"</li>
          <li>Set it to "Webhook" and paste this URL:</li>
        </ol>
        <div style={{ 
          marginTop: '0.5rem', 
          padding: '0.5rem', 
          backgroundColor: '#fff', 
          borderRadius: '4px', 
          fontFamily: 'monospace', 
          fontSize: '0.75rem',
          wordBreak: 'break-all'
        }}>
          {window.location.origin}/api/sms/webhook
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/api/sms/webhook`);
            toast.success('Webhook URL copied to clipboard!');
          }}
          style={{ marginTop: '0.5rem' }}
        >
          📋 Copy Webhook URL
        </Button>
      </div>
    </div>
  </div>
);

/**
 * SMS Templates Sub-section
 */
const SMSTemplatesSection = ({ settings, setSettings, API }) => (
  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
    <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>SMS Templates</h4>
    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
      Edit your quick message templates. Use these variables:{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{customer_name}'}</code>,{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{job_number}'}</code>,{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{status}'}</code>,{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{balance_owing}'}</code>,{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{business_name}'}</code>,{' '}
      <code style={{ backgroundColor: '#f3f4f6', padding: '0.1rem 0.3rem', borderRadius: '4px' }}>{'{business_phone}'}</code>
    </p>
    
    <div style={{ display: 'grid', gap: '1rem' }}>
      {(settings.sms_templates || []).map((template, index) => (
        <div key={index} style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <Input
              value={template.name}
              onChange={(e) => {
                const newTemplates = [...settings.sms_templates];
                newTemplates[index].name = e.target.value;
                setSettings({ ...settings, sms_templates: newTemplates });
              }}
              onBlur={async () => {
                try {
                  await axios.put(`${API}/settings`, { sms_templates: settings.sms_templates });
                  toast.success('Template name saved');
                } catch (error) {
                  toast.error('Failed to save');
                }
              }}
              style={{ fontWeight: '600', maxWidth: '200px' }}
              placeholder="Template Name"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={async () => {
                const newTemplates = settings.sms_templates.filter((_, i) => i !== index);
                try {
                  await axios.put(`${API}/settings`, { sms_templates: newTemplates });
                  setSettings({ ...settings, sms_templates: newTemplates });
                  toast.success('Template deleted');
                } catch (error) {
                  toast.error('Failed to delete');
                }
              }}
              style={{ color: '#dc2626' }}
            >
              Delete
            </Button>
          </div>
          <textarea
            value={template.message}
            onChange={(e) => {
              const newTemplates = [...settings.sms_templates];
              newTemplates[index].message = e.target.value;
              setSettings({ ...settings, sms_templates: newTemplates });
            }}
            onBlur={async () => {
              try {
                await axios.put(`${API}/settings`, { sms_templates: settings.sms_templates });
                toast.success('Template saved');
              } catch (error) {
                toast.error('Failed to save');
              }
            }}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '0.5rem',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              fontFamily: 'inherit',
              fontSize: '0.875rem',
              resize: 'vertical'
            }}
            placeholder="Message template..."
          />
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            {template.message.length}/160 characters
          </div>
        </div>
      ))}
      
      <Button
        variant="outline"
        onClick={async () => {
          const newTemplates = [...(settings.sms_templates || []), { name: 'New Template', message: '' }];
          try {
            await axios.put(`${API}/settings`, { sms_templates: newTemplates });
            setSettings({ ...settings, sms_templates: newTemplates });
            toast.success('New template added');
          } catch (error) {
            toast.error('Failed to add template');
          }
        }}
        data-testid="add-sms-template-btn"
      >
        + Add Template
      </Button>
    </div>
  </div>
);

export default SMSSettingsTab;
